<template>
  <div class="spinner spinner--show">
    <svg viewBox="0 0 200 300" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" class="spinner__graphic">
      <!-- Glass-effects (defs) -->
      <defs>
        <!-- Rim-gradients in four directions -->
        <linearGradient id="rimA" x1="0%" y1="0%" x2="100%" y2="0%" gradientUnits="objectBoundingBox" gradientTransform="rotate(30 .5 .5)">
          <stop offset="0" stop-color="white" stop-opacity=".45"/>
          <stop offset=".12" stop-color="white" stop-opacity=".24"/>
          <stop offset=".22" stop-color="white" stop-opacity=".06"/>
          <stop offset=".50" stop-color="white" stop-opacity="0"/>
          <stop offset=".78" stop-color="white" stop-opacity=".06"/>
          <stop offset=".88" stop-color="white" stop-opacity=".24"/>
          <stop offset="1" stop-color="white" stop-opacity=".45"/>
        </linearGradient>
        <linearGradient id="rimB" x1="0%" y1="0%" x2="100%" y2="0%" gradientUnits="objectBoundingBox" gradientTransform="rotate(210 .5 .5)">
          <stop offset="0" stop-color="white" stop-opacity=".45"/>
          <stop offset=".12" stop-color="white" stop-opacity=".24"/>
          <stop offset=".22" stop-color="white" stop-opacity=".06"/>
          <stop offset=".50" stop-color="white" stop-opacity="0"/>
          <stop offset=".78" stop-color="white" stop-opacity=".06"/>
          <stop offset=".88" stop-color="white" stop-opacity=".24"/>
          <stop offset="1" stop-color="white" stop-opacity=".45"/>
        </linearGradient>
        <linearGradient id="rimC" x1="0%" y1="0%" x2="100%" y2="0%" gradientUnits="objectBoundingBox" gradientTransform="rotate(90 .5 .5)">
          <stop offset="0" stop-color="white" stop-opacity=".45"/>
          <stop offset=".12" stop-color="white" stop-opacity=".24"/>
          <stop offset=".22" stop-color="white" stop-opacity=".06"/>
          <stop offset=".50" stop-color="white" stop-opacity="0"/>
          <stop offset=".78" stop-color="white" stop-opacity=".06"/>
          <stop offset=".88" stop-color="white" stop-opacity=".24"/>
          <stop offset="1" stop-color="white" stop-opacity=".45"/>
        </linearGradient>
        <linearGradient id="rimD" x1="0%" y1="0%" x2="100%" y2="0%" gradientUnits="objectBoundingBox" gradientTransform="rotate(330 .5 .5)">
          <stop offset="0" stop-color="white" stop-opacity=".45"/>
          <stop offset=".12" stop-color="white" stop-opacity=".24"/>
          <stop offset=".22" stop-color="white" stop-opacity=".06"/>
          <stop offset=".50" stop-color="white" stop-opacity="0"/>
          <stop offset=".78" stop-color="white" stop-opacity=".06"/>
          <stop offset=".88" stop-color="white" stop-opacity=".24"/>
          <stop offset="1" stop-color="white" stop-opacity=".45"/>
        </linearGradient>

        <!-- Inner specular + glow on rim -->
        <filter id="glassRimInner" x="-40%" y="-40%" width="180%" height="180%" color-interpolation-filters="sRGB">
          <feGaussianBlur in="SourceAlpha" stdDeviation="0.1" result="edgeBlur"/>
          <feComposite in="edgeBlur" in2="SourceAlpha" operator="in" result="edgeBlurIn"/>
          <feColorMatrix in="edgeBlurIn" type="matrix"
                         values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 0.6 0" result="haloIn"/>
          <feSpecularLighting in="edgeBlurIn" surfaceScale="2.5" specularConstant="0.6" specularExponent="18" lighting-color="white" result="spec">
            <fePointLight x="50" y="-86" z="80"/>
          </feSpecularLighting>
          <feComposite in="spec" in2="SourceAlpha" operator="in" result="specIn"/>
          <feBlend in="SourceGraphic" in2="haloIn" mode="screen" result="withGlow"/>
          <feBlend in="withGlow" in2="specIn" mode="screen" result="final"/>
        </filter>

        <!-- Highlights-only (for extra “glassy” look) -->
        <filter id="glassNoDark" x="-40%" y="-40%" width="180%" height="180%" color-interpolation-filters="sRGB">
          <feGaussianBlur in="SourceAlpha" stdDeviation="0.8" result="edgeBlur"/>
          <feComposite in="edgeBlur" in2="SourceAlpha" operator="in" result="edgeInner"/>
          <feSpecularLighting in="edgeInner" surfaceScale="0.4" specularConstant="0.5" specularExponent="18" lighting-color="white" result="spec">
            <fePointLight x="50" y="-86" z="120"/>
          </feSpecularLighting>
          <feComposite in="spec" in2="SourceAlpha" operator="in" result="specIn"/>
          <feBlend in="SourceGraphic" in2="edgeInner" mode="screen" result="g1"/>
          <feBlend in="g1" in2="specIn" mode="screen" result="final"/>
        </filter>

        <!-- Halo falloff + mask-ring (angled via rim-gradients) -->
        <radialGradient id="innerFalloff" cx="50%" cy="50%" r="60%">
          <stop offset="62%" stop-color="white" stop-opacity=".6"/>
          <stop offset="85%" stop-color="white" stop-opacity=".25"/>
          <stop offset="100%" stop-color="white" stop-opacity="0"/>
        </radialGradient>

        <mask id="maskA" maskUnits="objectBoundingBox" maskContentUnits="objectBoundingBox">
          <rect width="1" height="1" fill="black"/>
          <circle cx=".5" cy=".5" r=".5" fill="none" stroke="url(#rimA)" stroke-width=".07"/>
        </mask>
        <mask id="maskB" maskUnits="objectBoundingBox" maskContentUnits="objectBoundingBox">
          <rect width="1" height="1" fill="black"/>
          <circle cx=".5" cy=".5" r=".5" fill="none" stroke="url(#rimB)" stroke-width=".07"/>
        </mask>
        <mask id="maskC" maskUnits="objectBoundingBox" maskContentUnits="objectBoundingBox">
          <rect width="1" height="1" fill="black"/>
          <circle cx=".5" cy=".5" r=".5" fill="none" stroke="url(#rimC)" stroke-width=".07"/>
        </mask>
        <mask id="maskD" maskUnits="objectBoundingBox" maskContentUnits="objectBoundingBox">
          <rect width="1" height="1" fill="black"/>
          <circle cx=".5" cy=".5" r=".5" fill="none" stroke="url(#rimD)" stroke-width=".07"/>
        </mask>
      </defs>

      <!-- BUBBLES (glassy rim on circles) -->
      <g id="bubbles">
        <circle id="bubble-1"  class="glass-rim rimA tone-fern"    cx="98"  cy="270" r="4.5"/>
        <circle id="bubble-2"  class="glass-rim rimC tone-sage small-bubble" cx="102" cy="272" r="3.5"/>
        <circle id="bubble-3"  class="glass-rim rimB tone-emerald"     cx="94"  cy="274" r="3"/>
        <circle id="bubble-4"  class="glass-rim rimD tone-emerald" cx="106" cy="271" r="5"/>
        <circle id="bubble-5"  class="glass-rim rimA tone-fern-soft" cx="90"  cy="273" r="3.5"/>
        <circle id="bubble-6"  class="glass-rim rimC tone-sage"    cx="110" cy="275" r="3"/>
        <circle id="bubble-7"  class="glass-rim rimB tone-fern"    cx="96"  cy="276" r="4.5"/>
        <circle id="bubble-8"  class="glass-rim rimD tone-reef"    cx="104" cy="269" r="3.5"/>
        <circle id="bubble-9"  class="glass-rim rimA tone-emerald"     cx="92"  cy="271" r="3"/>
        <circle id="bubble-10" class="glass-rim rimC tone-fern"    cx="108" cy="272" r="4.5"/>
      </g>

      <!-- HALOS (separate layer following same animation)-->
      <g id="bubbles-halos" pointer-events="none">
        <circle class="halo haloA" cx="98"  cy="270" r="4.5"/>
        <circle class="halo haloC" cx="102" cy="272" r="3.5"/>
        <circle class="halo haloB" cx="94"  cy="274" r="3"/>
        <circle class="halo haloD" cx="106" cy="271" r="5"/>
        <circle class="halo haloA" cx="90"  cy="273" r="3.5"/>
        <circle class="halo haloC" cx="110" cy="275" r="3"/>
        <circle class="halo haloB" cx="96"  cy="276" r="4.5"/>
        <circle class="halo haloD" cx="104" cy="269" r="3.5"/>
        <circle class="halo haloA" cx="92"  cy="271" r="3"/>
        <circle class="halo haloC" cx="108" cy="272" r="4.5"/>
      </g>
    </svg>
  </div>
</template>

<script>
export default {
  name: "Spinner-vrak",
};
</script>
